'use client';

import { useEffect, useState } from 'react';

import {
  FALLBACK_PUBLIC_PRICING_SNAPSHOT,
  type PublicPricingSnapshot,
} from '@/lib/pricing/public-pricing';

let cachedSnapshot: PublicPricingSnapshot | null = null;
let pendingSnapshotPromise: Promise<PublicPricingSnapshot> | null = null;

/**
 * The pricing payload is admin-configured, so a response may legitimately omit
 * the add-on price map (or an individual add-on). Always keep the map present
 * and complete, falling back to the catalogue prices, so consumers can index
 * it without a guard and a partial payload cannot break the booking flow.
 */
function withAddOnsPricing(
  snapshot: Partial<PublicPricingSnapshot> | null | undefined,
): PublicPricingSnapshot {
  return {
    ...FALLBACK_PUBLIC_PRICING_SNAPSHOT,
    ...(snapshot ?? {}),
    addOnsPricing: {
      ...FALLBACK_PUBLIC_PRICING_SNAPSHOT.addOnsPricing,
      ...(snapshot?.addOnsPricing ?? {}),
    },
  };
}

async function loadSnapshot(): Promise<PublicPricingSnapshot> {
  if (cachedSnapshot) {
    return cachedSnapshot;
  }

  if (!pendingSnapshotPromise) {
    pendingSnapshotPromise = fetch('/api/config/pricing', {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    })
      .then(async response => {
        if (!response.ok) {
          throw new Error(`Failed to load public pricing: ${response.status}`);
        }
        return withAddOnsPricing((await response.json()) as Partial<PublicPricingSnapshot>);
      })
      .catch(() => withAddOnsPricing(null))
      .then(snapshot => {
        cachedSnapshot = snapshot;
        pendingSnapshotPromise = null;
        return snapshot;
      });
  }

  return pendingSnapshotPromise;
}

export function usePublicPricing() {
  const [snapshot, setSnapshot] = useState<PublicPricingSnapshot>(() =>
    withAddOnsPricing(cachedSnapshot),
  );

  useEffect(() => {
    let cancelled = false;

    void loadSnapshot().then(nextSnapshot => {
      if (!cancelled) {
        setSnapshot(nextSnapshot);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return snapshot;
}
